import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RIGHT_LABELS } from "./rights";
import {
  faIdBadge,
  faShieldHalved,
  faUserCircle,
  faRightToBracket,
  faChartBar,
  faClipboardList,
  faWrench,
  faArrowRight,
  faServer,
  faEnvelope,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";



/**
 * TDS Home Page (no Tailwind required)
 * - Self-contained styles (scoped <style> tag + inline styles)
 * - Accepts `user` prop from your auth payload
 *
 * Usage: <HomePage user={user} />
 */
export default function HomePage({ user }) {
  const rights = Array.isArray(user?.AccessRights) ? user.AccessRights : [];
  const hasRight = (r) => rights.includes(r);

  const friendlyServices = rights.map((r) => RIGHT_LABELS[r]).filter(Boolean);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };

  return (
    <main style={sx.main}>
      <ScopedStyles />

      {/* Hero / Greeting */}
      <section className="tds-card" style={{ marginBottom: 24 }}>
        <div style={sx.rowBetweenWrap}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src={user?.picture}
              alt={user?.name || "User"}
              style={sx.avatar}
            />
            <div>
              <h1 style={sx.h1}>Welcome back{user?.name ? ", " : ""}{user?.name || "User"}</h1>
              <p className="tds-subtle">Your TDS account overview and quick actions live here.</p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <a
              href={user?.email ? `mailto:${user.email}` : "mailto:"}
              className="tds-button secondary"
              style={{ textDecoration: "none" }}
            >
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 6 }} />
              {user?.email || "Contact"}
            </a>
            <button
              onClick={() => (window.location.href = "/logout")}
              className="tds-button primary"
            >
              <FontAwesomeIcon icon={faRightToBracket} style={{ marginRight: 6 }} /> Logout
            </button>
          </div>
        </div>
      </section>

      {/* Overview Cards */}
      <section style={sx.grid3}>
        <Card>
          <CardHeader icon={faUserCircle} title="Account" subtitle="Identity & basics" />
          <div className="tds-kv">
            <KV label="Name" value={user?.name || "—"} />
            <KV label="Type" value={user?.accounttype || "—"} />
            <KV label="Email" value={user?.email || "—"} />
            <div className="tds-kv-row">
              <span className="tds-kv-label">GUUID</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <code className="tds-code">{user?.GUUID || "—"}</code>
                {user?.GUUID && (
                  <button className="tds-chip" onClick={() => copy(user.GUUID)} title="Copy GUUID">
                    <FontAwesomeIcon icon={faCopy} style={{ marginRight: 6 }} /> Copy
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* <Card>
          <CardHeader icon={faShieldHalved} title="Security" subtitle="Manage access" />
          <ul className="tds-list">
            <li>
              <span>Passkeys & 2FA</span>
              <Link className="tds-link" to="/security/passkeys">Manage <FontAwesomeIcon icon={faArrowRight} /></Link>
            </li>
            <li>
              <span>Active Sessions</span>
              <Link className="tds-link" to="/security/sessions">View <FontAwesomeIcon icon={faArrowRight} /></Link>
            </li>
            <li>
              <span>API Tokens</span>
              <Link className="tds-link" to="/security/tokens">Create <FontAwesomeIcon icon={faArrowRight} /></Link>
            </li>
          </ul>
        </Card> */}

        <Card>
          <CardHeader icon={faIdBadge} title="Access Rights" subtitle="What you can use" />
          {rights.length === 0 ? (
            <p className="tds-subtle">No services have been assigned yet.</p>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {rights.map((r) => (
                <span key={r} className="tds-chip ghost">{r}</span>
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* Services */}
      <section style={{ marginBottom: 28 }}>
        <div style={sx.rowBetweenCenter}>
          <h2 style={sx.h2}>Your Services</h2>
          <Link to="/services" className="tds-link">All services <FontAwesomeIcon icon={faArrowRight} /></Link>
        </div>

        <div style={sx.grid3}
        >
          {friendlyServices.length ? (
            friendlyServices.map((svc) => (
              <ServiceCard key={svc.path} {...svc} />
            ))
          ) : (
            <div className="tds-card tds-card-dashed" style={{ gridColumn: "1 / -1", textAlign: "center" }}>
              No service entitlements detected yet. Once your account is granted access, they will appear here automatically.
            </div>
          )}

          {/* Always-available tools */}
          {/* <ServiceCard label="Requests" icon={faClipboardList} path="/requests" description="Open tickets and track progress." />
          <ServiceCard label="Systems" icon={faServer} path="/systems" description="Hosts & services overview." />
          <ServiceCard label="Tools" icon={faWrench} path="/tools" description="Utilities for power users." /> */}
        </div>
      </section>

      {/* Activity & Shortcuts */}
      <section style={sx.grid2}>
        <Card>
          <CardHeader icon={faChartBar} title="Recent Activity" subtitle="Last 7 days" />
          <div className="tds-empty">No recent activity to display.</div>
        </Card>

        <Card>
          <CardHeader icon={faWrench} title="Shortcuts" subtitle="Handy links" />
          <ul className="tds-bullets">
            {hasRight("net.thomasdye.TDS-reporting") && (
              <li><Link to="/reporting" className="tds-link">Open Reporting</Link></li>
            )}
            <li><Link to="/profile" className="tds-link">Edit Profile</Link></li>
            <li><Link to="/help" className="tds-link">Help & Docs</Link></li>
          </ul>
        </Card>
      </section>
    </main>
  );
}

/* ----------------------------- UI Primitives ----------------------------- */
function Card({ children }) {
  return <div className="tds-card">{children}</div>;
}

function CardHeader({ icon, title, subtitle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
      <div className="tds-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <div style={{ fontWeight: 600 }}>{title}</div>
        {subtitle && <div className="tds-subtle" style={{ fontSize: 12 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="tds-kv-row">
      <span className="tds-kv-label">{label}</span>
      <span className="tds-kv-value" title={String(value)}>{value}</span>
    </div>
  );
}

function ServiceCard({ label, icon, path, description }) {
  return (
    <Link to={path} className="tds-service">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <div className="tds-icon"><FontAwesomeIcon icon={icon} /></div>
        <div>
          <div style={{ fontWeight: 600 }}>{label}</div>
          {description && <div className="tds-subtle" style={{ fontSize: 12 }}>{description}</div>}
        </div>
      </div>
      <div className="tds-link">Open <FontAwesomeIcon icon={faArrowRight} /></div>
    </Link>
  );
}

/* -------------------------------- Styles -------------------------------- */
const sx = {
  main: { maxWidth: 1120, margin: "0 auto", padding: 16 },
  h1: { fontSize: 20, margin: 0, fontWeight: 600 },
  h2: { fontSize: 18, margin: 0, fontWeight: 600 },
  avatar: { width: 56, height: 56, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(0,0,0,0.06)" },
  rowBetweenWrap: { display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" },
  rowBetweenCenter: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(660px, 1fr))", gap: 16, marginBottom: 20 },
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 },
};

function ScopedStyles() {
  return (
    <style>{`
      /* Light/Dark neutral palette */
      :root { color-scheme: light dark; }
      .tds-card {
        background: rgba(255,255,255,0.7);
        backdrop-filter: blur(6px);
        border: 1px solid rgba(0,0,0,0.06);
        border-radius: 16px;
        padding: 16px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.06);
      }
      @media (prefers-color-scheme: dark) {
        .tds-card { background: rgba(30,30,30,0.5); border-color: rgba(255,255,255,0.15); }
      }
      .tds-card-dashed { border-style: dashed; text-align: center; padding: 24px; }
      .tds-subtle { color: rgba(0,0,0,0.6); }
      @media (prefers-color-scheme: dark) { .tds-subtle { color: rgba(255,255,255,0.7); } }

      .tds-icon { width: 40px; height: 40px; display: grid; place-items: center; border-radius: 10px; background: rgba(0,0,0,0.06); }
      @media (prefers-color-scheme: dark) { .tds-icon { background: rgba(255,255,255,0.1); } }

      .tds-kv { display: flex; flex-direction: column; gap: 6px; }
      .tds-kv-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
      .tds-kv-label { color: rgba(0,0,0,0.6); min-width: 90px; }
      @media (prefers-color-scheme: dark) { .tds-kv-label { color: rgba(255,255,255,0.7);} }
      .tds-kv-value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

      .tds-code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12px; background: rgba(0,0,0,0.06); padding: 4px 6px; border-radius: 6px; }
      @media (prefers-color-scheme: dark) { .tds-code { background: rgba(255,255,255,0.1); } }

      .tds-chip { border: 1px solid rgba(0,0,0,0.12); background: rgba(0,0,0,0.04); border-radius: 999px; padding: 4px 10px; font-size: 12px; cursor: pointer; }
      .tds-chip.ghost { background: transparent; }
      @media (prefers-color-scheme: dark) {
        .tds-chip { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.08); }
        .tds-chip.ghost { background: transparent; }
      }

      .tds-button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border-radius: 10px; padding: 8px 12px; font-size: 14px; cursor: pointer; border: 1px solid rgba(0,0,0,0.12); background: rgba(0,0,0,0.04); color: inherit; }
      .tds-button.primary { background: #000; color: #fff; border-color: #000; }
      .tds-button.primary:hover { opacity: 0.9; }
      @media (prefers-color-scheme: dark) { .tds-button.primary { background: #fff; color: #000; border-color: #fff; } }

      .tds-link { color: #0d47a1; text-decoration: none; }
      .tds-link:hover { text-decoration: underline; }
      @media (prefers-color-scheme: dark) { .tds-link { color: #8ab4ff; } }

      .tds-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
      .tds-list > li { display: flex; align-items: center; justify-content: space-between; gap: 8px; }

      .tds-bullets { margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 6px; }

      .tds-service { display: block; text-decoration: none; color: inherit; border: 1px solid rgba(0,0,0,0.06); border-radius: 16px; padding: 16px; background: rgba(255,255,255,0.7); transition: transform .12s ease, box-shadow .12s ease; }
      .tds-service:hover { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(0,0,0,0.08); }
      @media (prefers-color-scheme: dark) { .tds-service { background: rgba(30,30,30,0.5); border-color: rgba(255,255,255,0.15); } }

      .tds-empty { display: grid; place-items: center; height: 110px; border: 1px dashed rgba(0,0,0,0.12); border-radius: 12px; color: rgba(0,0,0,0.6); }
      @media (prefers-color-scheme: dark) { .tds-empty { border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.7); } }
    `}</style>
  );
}
